import React, { useState, useEffect } from 'react';
import { Drawer, Box, Button, useMediaQuery, useTheme } from '@mui/material';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { paddingContent } from '@europrocurement/flexy-components/assets';
import { ModalMenuProvider } from '@europrocurement/flexy-components/hooks/useModalMenuContext';
import { DesktopSidebar } from '../../molecules/DesktopSidebar/DesktopSidebar';
import { MenuItemProps, Scrollbar } from '../../molecules';
import { MobileBottomNavigation } from '../../molecules/MobileBottomNavigation/MobileBottomNavigation';
import { useMenuFiltering } from './hooks/useMenuFiltering';
import { useFullLayout } from '../../templates/FullLayout/FullLayoutContext';

export interface UserPreferencesProps {
    customMenuItems: MenuItemProps[];
    renderCurrentSelection?: () => React.JSX.Element;
    activeItem?: string;
}

/**
 * Modal de préférences utilisateur avec sidebar intégrée
 *
 * Composant convertit d'un drawer en modal centrée.
 * Responsive avec adaptation selon la taille d'écran.
 * Sur mobile, affiche une navigation en bas pour l'accessibilité au pouce.
 */
export const UserPreferences: React.FunctionComponent<UserPreferencesProps> = function ({
    customMenuItems,
    renderCurrentSelection,
    activeItem,
}) {
    const { isCustomizerDrawerOpen: showDrawer, setCustomizerDrawerOpen: setShowDrawer } =
        useFullLayout();

    const { palette, breakpoints, shadows } = useTheme();
    const isMobile = useMediaQuery(breakpoints.down('sm'));
    const isTablet = useMediaQuery(breakpoints.down('md'));
    const [activeTabMobile, setActiveTabMobile] = useState<string>('');

    // Hook pour filtrer les menus
    const { desktopMenuItems, mobileNavigationItems } = useMenuFiltering(customMenuItems);

    /**
     * Configuration responsive des dimensions de la modal
     */
    const getModalDimensions = () => {
        if (isMobile) {
            return {
                width: '95%',
                height: '95%',
                minHeight: '500px',
                minWidth: 'unset',
                maxWidth: 'unset',
            };
        }

        if (isTablet) {
            return {
                width: '85%',
                height: '85%',
                minHeight: '600px',
                minWidth: '600px',
                maxWidth: '900px',
            };
        }

        return {
            width: '900px',
            height: '700px',
            minHeight: '650px',
            minWidth: '724px',
            maxWidth: '90vw',
            maxHeight: '85vh',
        };
    };

    /**
     * Synchronise l'onglet actif mobile avec l'item actif global
     */
    useEffect(() => {
        if (isMobile) {
            if (
                activeItem &&
                mobileNavigationItems.some((item) => item.identifier === activeItem)
            ) {
                setActiveTabMobile(activeItem);
            } else if (mobileNavigationItems.length > 0 && !activeTabMobile) {
                // Fallback sur le premier item disponible
                setActiveTabMobile(mobileNavigationItems[0].identifier);
            }
        }
    }, [isMobile, mobileNavigationItems, activeItem, activeTabMobile]);

    const modalDimensions = getModalDimensions();

    return (
        <Drawer
            anchor="right"
            open={showDrawer}
            onClose={() => setShowDrawer(false)}
            PaperProps={{
                sx: {
                    ...modalDimensions,
                    borderRadius: '16px',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%) !important',
                    overflow: 'hidden',
                    boxShadow: shadows[24],
                },
            }}
            slotProps={{
                backdrop: {
                    sx: {
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    },
                },
            }}
        >
            {/* Provider pour le contexte modal des menus */}
            <ModalMenuProvider>
                {/* Bouton de fermeture */}
                <Button
                    onClick={() => setShowDrawer(false)}
                    sx={{
                        position: 'absolute',
                        top: '16px',
                        right: '16px',
                        zIndex: 10,
                        minWidth: 'unset',
                        width: '32px',
                        height: '32px',
                        padding: 0,
                        borderRadius: '8px',
                        '&:hover': {
                            backgroundColor:
                                palette.mode === 'light' ? palette.grey[100] : palette.grey[900],
                        },
                    }}
                    data-testid="close-button"
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            lineHeight: 1,
                            '& svg': {
                                fontSize: '18px !important',
                                verticalAlign: 'baseline',
                                display: 'block',
                                fontWeight: '600 !important',
                                strokeWidth: '2 !important',
                            },
                        }}
                    >
                        <FaOptionIcon
                            icon="close"
                            color={palette.text.secondary}
                        />
                    </Box>
                </Button>

                {/* Layout principal */}
                <Box
                    sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    {/* Zone principale avec sidebar et contenu */}
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'row',
                            minHeight: 0,
                        }}
                    >
                        {/* Sidebar desktop/tablet */}
                        {!isMobile && <DesktopSidebar menuItems={desktopMenuItems} />}

                        {/* Zone de contenu */}
                        <Box
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                overflow: 'hidden',
                            }}
                        >
                            <Scrollbar>
                                <Box
                                    sx={{
                                        padding: `${paddingContent * 2}px ${paddingContent}px ${isMobile ? paddingContent * 4 : paddingContent * 2}px ${paddingContent}px`,
                                    }}
                                >
                                    {renderCurrentSelection && renderCurrentSelection()}
                                </Box>
                            </Scrollbar>
                        </Box>
                    </Box>

                    {/* Navigation mobile */}
                    {isMobile && (
                        <MobileBottomNavigation
                            items={mobileNavigationItems}
                            activeTab={activeTabMobile}
                            onTabChange={setActiveTabMobile}
                        />
                    )}
                </Box>
            </ModalMenuProvider>
        </Drawer>
    );
};
